import { useConversationUuid } from '~/hooks'
import { cm, isCampaignUuid } from '~/utils/utils'
import { Block as BlockType } from '../chat/message'
import { DocMarkdown } from '../documents'
import { ContextBlock, ContextBlockProps } from './ContextBlock'
import { DividerBlock, DividerBlockProps } from './DividerBlock'
import { ErrorBlock, ErrorBlockProps } from './ErrorBlock'
import { RadioGroupBlock, RadioGroupBlockProps } from './RadioBlock'
import { ShimmerBlock } from './ShimmerBlock'
import { TaskBlock, TaskBlockProps } from './TaskBlock'
import { TimelineBlock, TimelineBlockProps } from './TimelineBlock'

interface BlockProps {
  block: BlockType
  className?: string
  childrenClassName?: string
  totalBlock?: number
  _uuid?: string
}

export function Block({ block, className, childrenClassName, totalBlock, _uuid }: BlockProps) {
  const uuid = useConversationUuid(_uuid)
  const childLength = block.children?.length
  function renderBlock(block: BlockType) {
    switch (block.type) {
      case 'image':
        return (
          <DocMarkdown
            key={block.id}
            text={`![image](data:image/png;base64,${block.text?.text})`}
          />
        )
      case 'pr':
        return (
          <DocMarkdown
            key={block.id}
            text={`[${block.text?.text}](${block.text?.text})`}
            className='pl-0'
          />
        )
      case 'task': {
        const isCampaign = isCampaignUuid(uuid)
        return isCampaign ? null : (
          <TaskBlock key={block.id} fields={block.fields as TaskBlockProps['fields']}>
            {block.children as BlockType[]}
          </TaskBlock>
        )
      }
      case 'context':
        return (
          <ContextBlock
            key={block.id}
            text={block.text}
            fields={block.fields as ContextBlockProps['fields']}
          />
        )
      case 'divider':
        return <DividerBlock key={block.id} fields={block.fields as DividerBlockProps['fields']} />
      case 'single-select':
        return (
          <RadioGroupBlock key={block.id} fields={block.fields as RadioGroupBlockProps['fields']} />
        )
      case 'error':
        return (
          <ErrorBlock
            key={block.id}
            text={block.text}
            fields={block.fields as ErrorBlockProps['fields']}
          />
        )
      case 'shimmer':
        return <ShimmerBlock key={block.id} value={block.value} />
      default:
        return (
          <DocMarkdown
            key={block.id}
            text={block.text?.text || ''}
            className={cm('pl-[20px]', childrenClassName)}
          />
        )
    }
  }

  if (block.type === 'timeline') {
    return (
      <TimelineBlock
        key={block.id}
        fields={block.fields as TimelineBlockProps['fields']}
        totalBlock={totalBlock || 1}
        _uuid={uuid}
      >
        {block.children as BlockType[]}
      </TimelineBlock>
    )
  }

  return (
    <div className={className}>
      {renderBlock(block)}
      <div className={cm('block-${block.id}-children', childrenClassName)}>
        {Array.isArray(block.children)
          ? block.children.map((childBlock: BlockType) => {
              return (
                <Block
                  key={childBlock.id}
                  block={childBlock}
                  className={childrenClassName}
                  totalBlock={childLength}
                  _uuid={uuid}
                />
              )
            })
          : null}
      </div>
    </div>
  )
}
